import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface LinePoint { label: string; value: number; }
export interface LineCoord {
  label: string; value: number; index: number;
  x: number; y: number;
}
export interface LineLayout {
  d: string; areaD: string;
  coords: LineCoord[];
  min: number; max: number;
}

export const LINE_VIEW_W = 240;
export const LINE_VIEW_H = 100;
export const LINE_PAD = 8;

export interface LineOpts {
  width?: number; height?: number; pad?: number;
  /** Explicit value domain, so two overlaid series share one scale.
   *  Omitted, each series is scaled to its own range. */
  min?: number; max?: number;
}

/**
 * Points to an SVG path. Pure, and dimension-parameterised so the sparkline
 * reuses it at a smaller size.
 *
 * A flat series (every value equal) draws through the vertical middle rather
 * than dividing by a zero range, and a single point centres rather than
 * dividing by a zero span.
 */
export function lineLayout(points: LinePoint[], opts: LineOpts = {}): LineLayout {
  const W = opts.width ?? LINE_VIEW_W;
  const H = opts.height ?? LINE_VIEW_H;
  const PAD = opts.pad ?? LINE_PAD;

  const rows = points ?? [];
  if (rows.length === 0) return { d: '', areaD: '', coords: [], min: 0, max: 0 };

  const values = rows.map(r => r.value);
  const min = opts.min ?? Math.min(...values);
  const max = opts.max ?? Math.max(...values);
  const range = max - min;

  const plotW = W - PAD * 2;
  const plotH = H - PAD * 2;

  const coords: LineCoord[] = rows.map((r, index) => ({
    label: r.label, value: r.value, index,
    x: rows.length === 1 ? W / 2 : PAD + (index / (rows.length - 1)) * plotW,
    y: range === 0 ? H / 2 : PAD + (1 - (r.value - min) / range) * plotH,
  }));

  const d = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)} ${c.y.toFixed(2)}`)
    .join(' ');

  const baseline = H - PAD;
  const areaD = `${d} L${coords[coords.length - 1].x.toFixed(2)} ${baseline} ` +
                `L${coords[0].x.toFixed(2)} ${baseline} Z`;

  return { d, areaD, coords, min, max };
}

@Component({
  selector: 'mm-line',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
  standalone: false,
})
export class LineChartComponent {
  @Input() points: LinePoint[] = [];
  /** Pre-formatted — charts never format money. */
  @Input() displayValues: string[] = [];
  @Input() areaFill = true;
  @Input() ariaLabel = 'Balance over time';
  /** Optional second series, drawn dashed against the same domain. */
  @Input() compare: LinePoint[] = [];
  /** Pre-formatted — charts never format money. */
  @Input() compareDisplayValues: string[] = [];
  /** Column heading for the compare series in the accessible data table. */
  @Input() compareLabel = 'Compare';

  @Output() select = new EventEmitter<number>();

  readonly viewW = LINE_VIEW_W;
  readonly viewH = LINE_VIEW_H;

  /**
   * The union of both series' values. Laying each series out against its own
   * min/max would draw them through the same pixels, so a portfolio far below
   * its target would look like it had already met it.
   */
  private get domain(): { min: number; max: number } | undefined {
    if (!this.compare.length) return undefined;
    const values = [...this.points, ...this.compare].map(p => p.value);
    if (!values.length) return undefined;
    return { min: Math.min(...values), max: Math.max(...values) };
  }

  get layout(): LineLayout { return lineLayout(this.points, this.domain ?? {}); }

  get compareLayout(): LineLayout { return lineLayout(this.compare, this.domain ?? {}); }

  get hasCompare(): boolean { return this.compare.length > 0; }

  /**
   * A dot is a real control only when a parent actually handles selection —
   * neither of this chart's consumers (accounts.component.html,
   * savings.component.html) binds (select), so without this every dot would
   * be a focusable "button" that does nothing, which is worse for a
   * screen-reader user than plain decoration.
   */
  get hasSelectListener(): boolean { return this.select.observed; }

  trackCoord(_: number, c: LineCoord): number { return c.index; }

  // preventDefault: Space on a focused element scrolls the page as well as
  // activating it.
  onSelect(c: LineCoord, event?: Event): void {
    event?.preventDefault();
    this.select.emit(c.index);
  }
}
